/**
 * FloatingActionButton Component
 *
 * Reusable floating action button (FAB) with consistent styling.
 * Supports two modes:
 * 1. Simple click: Pass onClick for single action
 * 2. Speed dial: Pass actions array for multiple options
 */

import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { AddIcon, CloseIcon } from '../icons';
import { Box, SpeedDial, SpeedDialAction, SpeedDialIcon } from '../ui';

interface SpeedDialActionItem {
  icon: React.ReactNode;
  name: string;
  onClick: () => void;
}

interface FloatingActionButtonProps {
  onClick?: () => void;
  actions?: SpeedDialActionItem[];
  size?: number;
  position?: { right: number; bottom: number };
  sx?: any;
}

const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({ onClick, actions, size = 56, position = { right: 50, bottom: 50 }, sx }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  // Speed dial mode: show actions menu
  if (actions && actions.length > 0) {
    return (
      <SpeedDial
        ariaLabel="Add actions"
        sx={{
          position: 'fixed',
          right: { xs: 16, sm: 24, md: position.right },
          bottom: { xs: 16, sm: 24, md: position.bottom },
          '& .MuiFab-primary': {
            backgroundColor: 'white',
            boxShadow: theme.custom.shadow.card,
            '&:hover': {
              backgroundColor: 'white',
              boxShadow: '0px 8px 24px -8px rgb(107, 107, 107)',
            },
          },
          ...sx,
        }}
        icon={
          <SpeedDialIcon icon={<AddIcon sx={{ color: theme.palette.primary.main }} />} openIcon={<CloseIcon sx={{ color: theme.palette.primary.main }} />} />
        }
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        direction="up"
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            slotProps={{ tooltip: { open: true, title: action.name, sx: { fontsize: '20px', whiteSpace: 'nowrap' } } }}
            onClick={() => {
              setOpen(false);
              action.onClick();
            }}
            sx={{
              '& .MuiSpeedDialAction-fab': { minWidth: 48, minHeight: 48 }, // Adjust minWidth and minHeight
              '& .MuiSpeedDialAction-staticTooltipLabel': {
                whiteSpace: 'nowrap',
              },
            }}
          />
        ))}
      </SpeedDial>
    );
  }

  // Simple mode: single click action
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: 'white',
        position: 'fixed',
        zIndex: 1000,
        right: { xs: 16, sm: 24, md: position.right },
        bottom: { xs: 16, sm: 24, md: position.bottom },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: theme.custom.shadow.card,
        cursor: 'pointer',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'scale(1.1)',
          boxShadow: '0px 8px 24px -8px rgb(107, 107, 107)',
        },
        ...sx,
      }}
      onClick={onClick}
    >
      <AddIcon sx={{ fontSize: size * 0.5, color: theme.palette.primary.main }} />
    </Box>
  );
};

export default FloatingActionButton;
